import axios from "axios";
import { getDeviceId } from "../deviceId";
import { API_BASE_URL } from "../../Components/constants";
import { withBasePath } from "../basePath";

axios.defaults.baseURL = API_BASE_URL;
axios.defaults.withCredentials = true;

const MIN_SKEW_MS = 120_000;
const SKEW_RATIO = 0.2;

// 인터셉트 미적용 (무한루프 방지)
const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});
const logoutClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// 401 에러가 동시에 발생할 경우 한 번의 /refresh로 처리
// 리프레시 요청 완료된 후에 새 AT를 대기자에게 전파하여 각 요청의 콜백을 실행
let isRefreshing = false;
let queue = [];
let hasLoggedOut = false;
let sessionExpiryTimerId = null;

const onRefreshed = (newAT) => {
  queue.forEach((cb) => cb(newAT));
  queue = [];
};
const addToQueue = (cb) => queue.push(cb);

function clearSessionExpiryTimer() {
  if (sessionExpiryTimerId) {
    clearTimeout(sessionExpiryTimerId);
    sessionExpiryTimerId = null;
  }
}

function clearAuthStorage() {
  clearSessionExpiryTimer();
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("accessTokenExpAt");
  localStorage.removeItem("refreshTokenExpAt");
  localStorage.removeItem("role");
  localStorage.removeItem("nickname");
}

function clearAppSessionStorage() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.clear();
  } catch {
    // Ignore storage access errors during logout cleanup.
  }
}

function loginPath(reason) {
  return withBasePath(
    reason ? `/login?reason=${encodeURIComponent(reason)}` : "/login"
  );
}

function parseExpMs(value) {
  if (value == null) return null;
  const n = Number(value);
  if (!Number.isNaN(n)) return n > 1e12 ? n : n * 1000;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

function sessionExpMsForRole(role, accessExpAtMs, refreshExpAt) {
  const currentRole = (role ?? "").toUpperCase();
  const parsedRefreshExpAtMs = parseExpMs(refreshExpAt);
  if (currentRole === "ADMIN" && parsedRefreshExpAtMs) {
    return parsedRefreshExpAtMs;
  }
  return accessExpAtMs;
}

function expireUserSessionIfNeeded() {
  const currentRole = (localStorage.getItem("role") ?? "").toUpperCase();
  const currentExpAtMs = Number(
    currentRole === "ADMIN"
      ? localStorage.getItem("refreshTokenExpAt")
      : localStorage.getItem("accessTokenExpAt")
  );

  if (
    (currentRole === "USER" || currentRole === "ADMIN") &&
    Number.isFinite(currentExpAtMs) &&
    currentExpAtMs &&
    currentExpAtMs <= Date.now() &&
    !hasLoggedOut
  ) {
    hasLoggedOut = true;
    logoutInternal({ reason: "session-expired" });
    return true;
  }
  return false;
}

function isUserAccessTokenExpired() {
  const currentRole = (localStorage.getItem("role") ?? "").toUpperCase();
  const currentExpAtMs = Number(localStorage.getItem("accessTokenExpAt"));
  return (
    currentRole === "USER" &&
    Number.isFinite(currentExpAtMs) &&
    currentExpAtMs &&
    currentExpAtMs <= Date.now()
  );
}

function rejectExpiredUserRefresh() {
  const err = new Error("refresh skipped");
  err.name = "RefreshSkipped";
  return err;
}

export function scheduleSessionExpiryLogout(role, expAtMs, refreshExpAt) {
  clearSessionExpiryTimer();

  const currentRole = (
    role ?? localStorage.getItem("role") ?? ""
  ).toUpperCase();
  const storedExpAtMs =
    currentRole === "ADMIN"
      ? localStorage.getItem("refreshTokenExpAt")
      : localStorage.getItem("accessTokenExpAt");
  const currentExpAtMs = Number(
    sessionExpMsForRole(currentRole, expAtMs ?? storedExpAtMs, refreshExpAt)
  );

  if (
    (currentRole !== "USER" && currentRole !== "ADMIN") ||
    !Number.isFinite(currentExpAtMs) ||
    !currentExpAtMs
  ) {
    return;
  }

  const delayMs = currentExpAtMs - Date.now();
  if (delayMs <= 0) {
    expireUserSessionIfNeeded();
    return;
  }

  sessionExpiryTimerId = setTimeout(() => {
    expireUserSessionIfNeeded();
  }, delayMs);
}

// ★ JWT exp(ms) 파서
function getJwtExpMs(accessToken) {
  try {
    const [, payloadB64] = accessToken.split(".");
    const json = JSON.parse(
      atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

// ★ 서버 payload로부터 만료시각(ms) 산출 (초/밀리초/ISO 다 수용)
function computeExpMsFromPayload(payload) {
  const v = payload?.accessTokenExpiresAt;
  if (v != null) {
    const n = Number(v);
    if (!Number.isNaN(n)) return n > 1e12 ? n : n * 1000; // 초면 ms로
    const t = Date.parse(v);
    if (!Number.isNaN(t)) return t;
  }
  const sec = payload?.accessTokenExpiresInSec ?? payload?.expiresIn;
  if (typeof sec === "number") return Date.now() + sec * 1000;
  return null;
}

// 리프레시 한 번만 수행하는 함수
async function refreshOnce() {
  if (isUserAccessTokenExpired()) {
    expireUserSessionIfNeeded();
    throw rejectExpiredUserRefresh();
  }

  const rt = localStorage.getItem("refreshToken");

  // 선행 리프레시 중이면 대기열
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      addToQueue((newAT) =>
        newAT ? resolve(newAT) : reject(new Error("refresh failed"))
      );
    });
  }

  isRefreshing = true;

  try {
    const refreshPayload = { deviceId: getDeviceId() };
    if (rt) refreshPayload.refreshToken = rt;

    const { data } = await refreshClient.post("/api/auth/refresh", refreshPayload);

    const payload = data.data;
    const newAT = payload.accessToken;

    if (!newAT) throw new Error("No accessToken from refresh");

    // ★ 만료시각: JWT exp → payload → fallback 순으로 계산
    let expMs =
      getJwtExpMs(newAT) ??
      computeExpMsFromPayload(payload) ??
      Date.now() + 1 * 50 * 1000; // 안전망 5분

    localStorage.setItem("accessToken", payload.accessToken);
    localStorage.setItem("accessTokenExpAt", String(expMs));
    localStorage.removeItem("refreshToken");
    localStorage.setItem("role", payload.role);

    const sessionExpMs = sessionExpMsForRole(
      payload.role,
      expMs,
      payload.refreshTokenExpiresAt,
    );
    if ((payload.role ?? "").toUpperCase() === "ADMIN" && sessionExpMs) {
      localStorage.setItem("refreshTokenExpAt", String(sessionExpMs));
    } else {
      localStorage.removeItem("refreshTokenExpAt");
    }
    scheduleSessionExpiryLogout(payload.role, expMs, payload.refreshTokenExpiresAt);

    isRefreshing = false;
    onRefreshed(newAT);

    return newAT;
  } catch (error) {
    isRefreshing = false;
    onRefreshed(null);

    const status = error?.response?.status;
    const err = new Error("refresh failed");

    if (status === 400 || status === 401) err.name = "RefreshUnauthorized";
    throw err;
  }
}

// 요청 직전에 만료 임박이면 선제 리프레시
async function freshAccessToken() {
  const expAtStr = localStorage.getItem("accessTokenExpAt");
  const expAt = Number(expAtStr);
  const now = Date.now();
  const at = localStorage.getItem("accessToken");

  if (!at) {
    return;
  }

  if (!expAt) {
    await refreshOnce();
    return;
  }

  if (isUserAccessTokenExpired()) {
    expireUserSessionIfNeeded();
    return;
  }

  const ttl = expAt - now; // 남은 수명
  const dynamicSkew = Math.max(MIN_SKEW_MS, Math.floor(ttl * SKEW_RATIO));
  if (ttl <= dynamicSkew) {
    await refreshOnce();
  }
}

// WebSocket 등에서 필요할 때 선제적으로 토큰을 갱신할 수 있도록 노출
export async function ensureFreshAccessToken() {
  try {
    await freshAccessToken();
  } catch {
    // 호출 측에서 401/재로그인 흐름을 처리하도록 조용히 무시
  }
}

// WebSocket 재연결 시 강제 갱신 용도
export async function forceRefreshAccessToken() {
  return refreshOnce();
}

// 1) 요청 인터셉터: 선제 리프레시 + AT 붙이기
axios.interceptors.request.use(
  async (config) => {
    try {
      await freshAccessToken(); // ← 만료 임박이면 미리 갱신
    } catch {
      // 선제 리프레시 실패 시엔 401 흐름에서 처리하게 그냥 둠
    }
    const at = localStorage.getItem("accessToken");

    if (at) config.headers.Authorization = `Bearer ${at}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// 2) 응답 인터셉터: 401 → 리프레시 → 재시도
axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    const { config, response } = err;
    if (!response) return Promise.reject(err); // 네트워크/타임아웃 등

    // ★ /refresh 자체가 401이면 재시도 금지
    if (config?.url?.includes("/api/auth/refresh")) {
      return Promise.reject(err);
    }

    // 401만 처리 + 무한루프 방지
    if (response.status !== 401 || config.__isRetry) {
      return Promise.reject(err);
    }

    if (isUserAccessTokenExpired()) {
      expireUserSessionIfNeeded();
      return Promise.reject(err);
    }

    try {
      if (!localStorage.getItem("accessToken")) {
        return Promise.reject(err);
      }
      const newAT = await refreshOnce();
      config.__isRetry = true;
      config.headers.Authorization = `Bearer ${newAT}`;
      return axios(config);
    } catch (e) {
      const status = e?.response?.status;

      if (
        (!status || status >= 500 || e.name === "RefreshUnauthorized") &&
        !hasLoggedOut
      ) {
        hasLoggedOut = true;
        await logoutInternal({ reason: "session-expired" });
      }

      return Promise.reject(e);
    }
  }
);

// 로그아웃
async function logoutInternal({ reason, redirect = true } = {}) {
  const at = localStorage.getItem("accessToken");
  try {
    await logoutClient.post(
      "/api/auth/logout",
      { deviceId: getDeviceId() },
      { headers: { Authorization: `Bearer ${at}` } }
    );
  } catch (error) {
    console.log("로그아웃 실패: ", error);
  } finally {
    clearAuthStorage();
    clearAppSessionStorage();
    if (redirect) {
      window.location.replace(loginPath(reason));
    }
  }
}

export const logout = logoutInternal;

export const logoutAll = async () => {
  const at = localStorage.getItem("accessToken");

  try {
    await logoutClient.post(
      "/api/auth/logout-all",
      {},
      { headers: { Authorization: `Bearer ${at}` } }
    );
  } catch (error) {
    console.log("logout-all failed: ", error);
  } finally {
    clearAuthStorage();
    clearAppSessionStorage();
    window.location.replace(loginPath());
  }
};

scheduleSessionExpiryLogout();
window.addEventListener("focus", expireUserSessionIfNeeded);
document.addEventListener("visibilitychange", () => {
  if (!document.hidden) expireUserSessionIfNeeded();
});

export default axios;
